import { useSession } from "@supabase/auth-helpers-react";
import { useImpersonation } from "../../state/providers/ImpersonationProvider";

/**
 * Hook that returns user data that respects impersonation state
 * Returns impersonated user data when impersonating, otherwise returns current user data
 */
export const useImpersonationAwareUser = () => {
    const session = useSession();
    const { impersonationSession, isImpersonating } = useImpersonation();

    const getEffectiveUserId = (): string | null => {
        if (isImpersonating && impersonationSession?.targetUserId) {
            return impersonationSession.targetUserId;
        }
        return session?.user?.id || null;
    };

    const getEffectiveUserEmail = (): string | null => {
        if (isImpersonating && impersonationSession?.targetEmail) {
            return impersonationSession.targetEmail;
        }
        return session?.user?.email || null;
    };

    const getAdminUserId = (): string | null => {
        if (isImpersonating && impersonationSession?.adminUserId) {
            return impersonationSession.adminUserId;
        }
        return session?.user?.id || null;
    };

    const getAdminUserEmail = (): string | null => {
        if (isImpersonating && impersonationSession?.adminEmail) {
            return impersonationSession.adminEmail;
        }
        return session?.user?.email || null;
    };

    return {
        // Current user (what the app should use for data access)
        userId: getEffectiveUserId(),
        userEmail: getEffectiveUserEmail(),

        // Admin user (who is actually logged in)
        adminUserId: getAdminUserId(),
        adminEmail: getAdminUserEmail(),

        // Impersonation state
        isImpersonating,
        impersonationSession,

        // Original session data
        originalSession: session,
    };
};
